import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiShield, FiTruck, FiZap } from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import { registerUserAccount } from "../../services/registerApi";
import "./RegisterForms.css";
import useCompanySuggestions from "./useCompanySuggestions";
import useReferralAttribution from "./useReferralAttribution";
import { buildReferralLink } from "../../utils/referralAttribution";
import { PHONE_COUNTRY_OPTIONS } from "../../constants/phoneCountryCodes";

const initialState = {
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  phoneCountry: PHONE_COUNTRY_OPTIONS[0].value,
  company: "",
  password: "",
  confirmPassword: "",
  taxId: "",
  referralCode: ""
};

const buildInitialState = referralCode => ({
  ...initialState,
  referralCode: referralCode || ""
});

const RegisterClient = ({ onGoogleSignIn, googleSignInPending = false }) => {
  const { referral, clearReferral } = useReferralAttribution();
  const [values, setValues] = useState(() => buildInitialState(referral?.code));
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const navigate = useNavigate();
  const trimmedCompany = values.company.trim();
  const hasExactMatch =
    selectedCompany &&
    trimmedCompany.toLowerCase() === (selectedCompany.name || "").toLowerCase();
  const { suggestions, loading } = useCompanySuggestions(values.company, {
    enabled: !submitting && !hasExactMatch
  });
  const showSuggestions = !selectedCompany && trimmedCompany.length >= 2;

  useEffect(() => {
    document.body.classList.add("hide-topbar");
    return () => document.body.classList.remove("hide-topbar");
  }, []);

  useEffect(() => {
    if (!referral?.code) {
      return;
    }
    setValues(prev =>
      prev.referralCode
        ? prev
        : {
            ...prev,
            referralCode: referral.code
          }
    );
  }, [referral?.code]);

  const handleFieldChange = field => event => {
    const { value } = event.target;
    setValues(prev => ({ ...prev, [field]: value }));
    if (field === "company") {
      setSelectedCompany(prev =>
        prev && value.trim().toLowerCase() === (prev.name || "").toLowerCase() ? prev : null
      );
    }
  };
  const handlePhoneCountryChange = event => {
    const { value } = event.target;
    setValues(prev => ({ ...prev, phoneCountry: value }));
  };

  const handleSelectCompany = option => {
    if (!option) return;
    setSelectedCompany(option);
    setValues(prev => ({ ...prev, company: option.name || "" }));
  };

  const handleClearSelectedCompany = () => {
    setSelectedCompany(null);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (submitting) return;

    const trimmedValues = Object.fromEntries(
      Object.entries(values).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value])
    );

    const {
      password,
      confirmPassword,
      firstName,
      lastName,
      email,
      phone,
      phoneCountry,
      company,
      taxId,
      referralCode
    } = trimmedValues;
    const requiredFields = [firstName, lastName, email, phone, password, confirmPassword];

    if (requiredFields.some(value => !value)) {
      setError("Por favor completa los campos obligatorios antes de continuar.");
      return;
    }

    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden. Revisa los campos e intenta de nuevo.");
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      const matchesExisting =
        selectedCompany &&
        trimmedValues.company.toLowerCase() === (selectedCompany.name || "").toLowerCase();
      const typedReferralCode = referralCode || "";
      const normalizedReferralCode = typedReferralCode.trim() || referral?.code || "";
      const referralLink =
        normalizedReferralCode && referral?.code === normalizedReferralCode
          ? referral?.link || ""
          : normalizedReferralCode
          ? buildReferralLink(normalizedReferralCode)
          : "";
      const selectedPhoneCountry =
        phoneCountry || values.phoneCountry || PHONE_COUNTRY_OPTIONS[0].value;
      const formattedPhone = `${selectedPhoneCountry}${phone.replace(/[\s-]/g, "")}`;
      await registerUserAccount({
        role: "client",
        firstName,
        lastName,
        email,
        phone: formattedPhone,
        company,
        password,
        companyTaxId: taxId,
        existingCompanyId: matchesExisting ? selectedCompany.id : "",
        referralCode: normalizedReferralCode,
        referralLink
      });
      clearReferral();
      setValues(buildInitialState());
      setSelectedCompany(null);
      navigate("/welcome", { replace: true });
    } catch (registrationError) {
      const message =
        registrationError?.message || "No pudimos crear la cuenta en este momento. Intenta nuevamente.";
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <main className="register-page">
      <div className="register-card">
        <div className="register-card__brand">
          <p className="register-card__logo">FLOTTE</p>
          <div>
            <h2>Gestiona tu logística sin límites.</h2>
            <p>
              Digitaliza solicitudes, coordina envíos y colabora con proveedores verificados desde un solo panel.
            </p>
          </div>
          <ul className="register-card__list">
            <li>
              <FiZap aria-hidden="true" />
              Responde solicitudes en cuestión de minutos.
            </li>
            <li>
              <FiTruck aria-hidden="true" />
              Asigna capacidad con visibilidad total.
            </li>
          </ul>
          <div className="register-card__badge">
            <FiShield aria-hidden="true" />
            <span>Rated 4.9 / 5 por empresas LATAM</span>
          </div>
        </div>
        <div className="register-card__form">
          <Link to="/register" className="register-card-back">
            <FiArrowLeft aria-hidden="true" />
            Volver
          </Link>
          <header className="register-form-header">
            <p className="register-form-eyebrow">Clientes Flotte</p>
            <h1>Crea tu cuenta de Cliente</h1>
            <p>Completa la información para crear tu cuenta corporativa.</p>
          </header>

          {error ? (
            <div className="register-form-error" role="alert">
              {error}
            </div>
          ) : null}

          <div className="register-social">
            <button
              type="button"
              className="register-google-btn"
              onClick={onGoogleSignIn}
              disabled={!onGoogleSignIn || googleSignInPending || submitting}
            >
              <FcGoogle aria-hidden="true" />
              <span>{googleSignInPending ? "Conectando con Google…" : "Continuar con Google"}</span>
            </button>
            <div className="register-social-divider" role="separator" aria-label="O completa el formulario">
              <span>o completa el formulario</span>
            </div>
          </div>

          <form className="register-form" onSubmit={handleSubmit} noValidate>
            <section className="register-form-section">
              <p className="register-section-label">Datos de contacto</p>
              <div className="register-field-grid register-field-grid--dense">
                <div className="register-field">
                  <label className="register-field-label" htmlFor="client-first-name">
                    Nombre
                  </label>
                  <input
                    id="client-first-name"
                    type="text"
                    name="firstName"
                    placeholder="Nombre"
                    value={values.firstName}
                    onChange={handleFieldChange("firstName")}
                    autoComplete="given-name"
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="register-field">
                  <label className="register-field-label" htmlFor="client-last-name">
                    Apellido
                  </label>
                  <input
                    id="client-last-name"
                    type="text"
                    name="lastName"
                    placeholder="Apellido"
                    value={values.lastName}
                    onChange={handleFieldChange("lastName")}
                    autoComplete="family-name"
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="register-field register-field--full">
                  <label className="register-field-label" htmlFor="client-email">
                    Correo electrónico
                  </label>
                  <input
                    id="client-email"
                    type="email"
                    name="email"
                    placeholder="nombre@empresa.com"
                    value={values.email}
                    onChange={handleFieldChange("email")}
                    autoComplete="email"
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="register-field">
                  <label className="register-field-label" htmlFor="client-phone">
                    Número de teléfono
                  </label>
                  <div className="register-phone-input">
                    <select
                      aria-label="Prefijo del país"
                      value={values.phoneCountry}
                      onChange={handlePhoneCountryChange}
                      disabled={submitting}
                    >
                      {PHONE_COUNTRY_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <input
                      id="client-phone"
                      type="tel"
                      name="phone"
                      placeholder="55 1234 5678"
                      value={values.phone}
                      onChange={handleFieldChange("phone")}
                      autoComplete="tel"
                      pattern="[0-9\\s-]{7,20}"
                      required
                      disabled={submitting}
                    />
                  </div>
                  <span className="register-field-helper">
                    Ingresa solo los dígitos del número; el prefijo se agrega automáticamente.
                  </span>
                </div>
                <div className="register-field">
                  <label className="register-field-label" htmlFor="client-tax-id">
                    RFC (opcional)
                  </label>
                  <input
                    id="client-tax-id"
                    type="text"
                    name="taxId"
                    placeholder="RFC"
                    value={values.taxId}
                    onChange={handleFieldChange("taxId")}
                    autoComplete="off"
                    disabled={submitting}
                  />
                </div>
              </div>
            </section>

            <section className="register-form-section">
              <p className="register-section-label">Datos de la empresa</p>
              <div className="register-field-grid register-field-grid--dense">
                <div className="register-field register-field--full">
                  <label className="register-field-label" htmlFor="client-company">
                    Nombre de la empresa (opcional)
                  </label>
                  <input
                    id="client-company"
                    type="text"
                    name="company"
                    placeholder="Empresa Logística SA de CV"
                    value={values.company}
                    onChange={handleFieldChange("company")}
                    autoComplete="organization"
                    disabled={submitting}
                  />
                  {selectedCompany ? (
                    <div className="register-company-selected" role="note">
                      <p>
                        Solicitarás acceso a <strong>{selectedCompany.name}</strong>
                        {selectedCompany.location ? ` (${selectedCompany.location})` : ""}. El administrador
                        de la empresa recibirá una solicitud para aprobar tu ingreso.
                      </p>
                      <button
                        type="button"
                        className="register-company-selected__action"
                        onClick={handleClearSelectedCompany}
                        disabled={submitting}
                      >
                        Elegir otra empresa
                      </button>
                    </div>
                  ) : null}
                  {showSuggestions ? (
                    <div className="register-company-suggestions" role="listbox" aria-label="Empresas registradas">
                      {loading ? (
                        <div className="register-company-suggestions__loading">Buscando empresas...</div>
                      ) : suggestions.length > 0 ? (
                        suggestions.map(option => (
                          <button
                            key={option.id}
                            type="button"
                            className="register-company-suggestion"
                            onClick={() => handleSelectCompany(option)}
                            role="option"
                            aria-selected="false"
                          >
                            <span className="register-company-suggestion__name">{option.name}</span>
                            {option.location ? (
                              <span className="register-company-suggestion__location">{option.location}</span>
                            ) : null}
                          </button>
                        ))
                      ) : trimmedCompany.length >= 3 ? (
                        <div className="register-company-suggestions__empty">
                          No encontramos coincidencias. Escribe el nombre completo de tu empresa para crearla.
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </section>

            <section className="register-form-section">
              <p className="register-section-label">Acceso</p>
              <div className="register-field-grid register-field-grid--dense">
                <div className="register-field">
                  <label className="register-field-label" htmlFor="client-password">
                    Contraseña
                  </label>
                  <input
                    id="client-password"
                    type="password"
                    name="password"
                    placeholder="********"
                    value={values.password}
                    onChange={handleFieldChange("password")}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    disabled={submitting}
                  />
                  <span className="register-field-helper">
                    Usa al menos 8 caracteres. Combina letras, números y símbolos.
                  </span>
                </div>

                <div className="register-field">
                  <label className="register-field-label" htmlFor="client-confirm-password">
                    Confirmar contraseña
                  </label>
                  <input
                    id="client-confirm-password"
                    type="password"
                    name="confirmPassword"
                    placeholder="********"
                    value={values.confirmPassword}
                    onChange={handleFieldChange("confirmPassword")}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    disabled={submitting}
                  />
                  <span className="register-field-helper">
                    Repite la contraseña exactamente como la escribiste arriba.
                  </span>
                </div>
              </div>
            </section>

            <section className="register-form-section">
              <p className="register-section-label">Referidos</p>
              <div className="register-field register-field--full">
                <label className="register-field-label" htmlFor="client-referral-code">
                  Código de referido (opcional)
                </label>
                <input
                  id="client-referral-code"
                  type="text"
                  name="referralCode"
                  placeholder="MIEMPRESA"
                  value={values.referralCode}
                  onChange={handleFieldChange("referralCode")}
                  autoComplete="off"
                  disabled={submitting}
                  maxLength={14}
                />
                <span className="register-field-helper">
                  Si te compartieron Flotte, ingresa su código para que ambos reciban 120 créditos.
                </span>
              </div>
            </section>

            <div className="register-form-actions">
              <button type="submit" className="register-submit-btn" disabled={submitting}>
                {submitting ? "Creando cuenta..." : "Crear cuenta"}
              </button>
              <p className="register-form-note">
                ¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
};

export default RegisterClient;
